import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, FileText, Users, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Home = () => {
  const services = [
    {
      icon: TrendingUp,
      title: 'Wealth Management',
      description: 'Strategic portfolio management tailored to your unique financial goals and risk tolerance.',
      link: '/asset-management',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
    },
    {
      icon: FileText,
      title: 'Tax',
      description: 'Complete tax services including preparation, planning, and optimization with our in-house CPAs nationwide.',
      link: '/tax',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85',
    },
    {
      icon: Shield,
      title: 'Estate',
      description: 'Comprehensive estate planning, trusts, and wills with our attorneys available in all 50 states.',
      link: '/estate',
      image: 'https://images.unsplash.com/photo-1562564055-71e051d33c19',
    },
  ];

  const stats = [
    { value: 'FINRA', label: 'Registered Fiduciary' },
    { value: '3', label: 'Core Services - One Firm' },
    { value: '45+', label: 'Years Combined Experience' },
    { value: '50', label: 'Serving All 50 States' },
  ];

  const whyChooseUs = [
    {
      icon: Users,
      title: 'Truly Holistic Approach',
      description: 'The only firm offering comprehensive wealth management, tax, and estate services under one roof.',
    },
    {
      icon: Award,
      title: 'Licensed Professionals',
      description: 'All advisors are fully licensed, backed by in-house CPAs and estate attorneys.',
    },
    {
      icon: Shield,
      title: 'Client Reviews',
      description: 'See what our clients say about their experience with Paracca Financial Group.',
      link: 'https://share.google/AobmTPd4bJkmtnijY',
    },
    {
      icon: CheckCircle,
      title: 'Client-Centric Service',
      description: 'Personalized strategies designed specifically for individuals and families.',
    },
  ];

  // Comprehensive Schema.org structured data for AI search engines
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["FinancialService", "LocalBusiness", "ProfessionalService"],
        "@id": "https://www.paracca.com/#organization",
        "name": "Paracca Financial Group",
        "alternateName": "Paracca Financial",
        "url": "https://www.paracca.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.paracca.com/logo.png"
        },
        "image": "https://www.paracca.com/logo.png",
        "description": "Paracca Financial Group is a FINRA registered fiduciary investment advisory firm providing comprehensive wealth management services nationwide. We offer wealth management with options overlay strategies, tax preparation and planning with in-house CPAs, and estate planning with licensed attorneys available in all 50 states.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "428 S Tamiami Trail",
          "addressLocality": "Osprey",
          "addressRegion": "FL",
          "postalCode": "34229",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "27.1836",
          "longitude": "-82.3248"
        },
        "telephone": "+1-941-545-9057",
        "email": "contact@paracca.com",
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "priceRange": "$$$",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:30",
            "closes": "16:00"
          }
        ],
        "sameAs": [
          "https://www.linkedin.com/company/paracca-financial-group",
          "https://www.facebook.com/paraccafinancial"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Financial Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Wealth Management",
                "description": "Proprietary options overlay strategy for portfolio management and wealth preservation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Tax Services",
                "description": "Tax preparation, planning, and optimization with in-house CPAs nationwide"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Estate Planning",
                "description": "Comprehensive estate planning, trusts, and wills with licensed attorneys in all 50 states"
              }
            }
          ]
        },
        "knowsAbout": [
          "Investment Advisory",
          "Wealth Management",
          "Financial Planning",
          "Tax Planning",
          "Estate Planning",
          "Retirement Planning",
          "Wealth Management",
          "Portfolio Management",
          "Options Trading",
          "Fiduciary Services"
        ],
        "slogan": "Your Wealth, Our Expertise"
      },
      {
        "@type": "WebSite",
        "@id": "https://www.paracca.com/#website",
        "url": "https://www.paracca.com",
        "name": "Paracca Financial Group",
        "description": "Financial advisory firm serving Nationwide, FL",
        "publisher": {
          "@id": "https://www.paracca.com/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.paracca.com/?s={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <>
      <SEO schema={schema} />
      
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets-39nsmqrw.emergentagent.net/job_estate-wealth-tax/artifacts/wp5ths92_0b29653f-acce-4df4-8367-4782aaf1fa90.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-60"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl lg:text-7xl  text-white mb-6 uppercase leading-tight" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>
              Where Every Financial Decision is Made With Confidence
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-white mb-8 leading-relaxed" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
              You should expect more from your financial advice. With wealth management, tax, and estate planning under one roof, every part of your financial life is coordinated by a single team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold text-lg px-8 py-6 transition-all duration-300 hover:shadow-2xl group"
                asChild
              >
                <Link to="/contact">
                  Get Started
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0a69b2] font-montserrat font-semibold text-lg px-8 py-6 transition-all duration-300"
                asChild
              >
                <a href="tel:941-545-9057">
                  Call 941-545-9057
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-bellefair text-4xl md:text-5xl  text-[#0a69b2] mb-2">
                  {stat.value}
                </div>
                <div className="font-montserrat text-sm md:text-base text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Founder Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_estate-wealth-tax/artifacts/fwzmdio8_IMG_6177.PNG"
                  alt="Financial Advisor Nationwide Service - Don Paracca Wealth Management Nationwide"
                  className="rounded-lg shadow-2xl w-full"
                />
              </div>
            </div>

            {/* Founder Content */}
            <div className="order-1 lg:order-2">
              <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
                Meet Our Founder
              </h2>
              <h3 className="font-bellefair text-2xl  text-gray-700 mb-6 uppercase">
                Don Paracca, MBA
              </h3>
              <p className="font-montserrat text-sm font-semibold text-[#0a69b2] mb-6 tracking-wide uppercase">
                President & CEO
              </p>
              
              <div className="space-y-4 font-montserrat text-lg text-gray-600 leading-relaxed">
                <p>
                  Don Paracca founded Paracca Financial Group after building extensive experience at a Fortune 300 financial services firm, where he witnessed firsthand the limitations of large-scale financial institutions. While traditional firms excel at scale, they often struggle to provide the personalized attention and integrated approach that individuals and families truly need.
                </p>
                <p>
                  Recognizing this gap in the market, Don established Paracca Financial Group with a revolutionary vision: to deliver boutique-level service with enterprise-grade expertise. By bringing wealth management, tax services, and estate services under one roof, he created the region's only truly holistic financial advisory firm—eliminating the inefficiencies of working with multiple disconnected providers.
                </p>
                <p>
                  Today, with licensed advisors, in-house CPAs, and estate attorneys available in all 50 states, Paracca Financial Group delivers the coordinated, comprehensive wealth management that Don envisioned from the beginning—where every aspect of your financial life works together seamlessly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-4xl md:text-5xl  text-[#0a69b2] mb-4 uppercase">
              Our Services
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive financial solutions designed for individuals and families
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={service.title}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#0a69b2] overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-[#0a69b2] opacity-20 group-hover:opacity-30 transition-opacity"></div>
                  </div>
                  <CardContent className="p-6">
                    <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-3 uppercase">
                      {service.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <Link 
                      to={service.link}
                      className="inline-flex items-center font-montserrat font-semibold text-[#0a69b2] hover:text-[#0858a0] transition-colors group"
                    >
                      Learn More
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-4xl md:text-5xl  text-[#0a69b2] mb-4 uppercase">
              Why Choose Paracca Financial Group
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the difference of working with a truly integrated financial services firm
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={item.title}
                  className="flex gap-4 p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex-shrink-0">
                    <div className="bg-[#0a69b2] w-12 h-12 rounded-lg flex items-center justify-center">
                      <IconComponent className="text-white" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-2 uppercase">
                      {item.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed mb-3">
                      {item.description}
                    </p>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#0a69b2] hover:text-[#0858a0] font-montserrat font-semibold text-sm transition-colors"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Read Our Reviews
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-4xl md:text-5xl  mb-6 uppercase">
            Ready to Secure Your Financial Future?
          </h2>
          <p className="font-montserrat text-lg md:text-xl mb-8 leading-relaxed text-gray-100">
            Schedule a complimentary consultation with our team of expert advisors
          </p>
          <Button 
            size="lg"
            className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold text-lg px-10 py-6 transition-all duration-300 hover:shadow-2xl"
            asChild
          >
            <a href="tel:941-545-9057">
              Contact Us Today
            </a>
          </Button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-4xl md:text-5xl  text-[#0a69b2] mb-4 uppercase">
              Get In Touch
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              We're here to help you achieve your financial goals
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default Home;
